import React, { useContext } from 'react'
import { AuthContext } from '../../../context/AuthContext'
import { CreditCard, Calendar, CheckCircle } from 'lucide-react'

function Plan() {
    const {
        isSubscribed,
        subScriptionEndDate,
        plan,
        subScriptionAmount,
        paymentMethod,
        planStatus
    } = useContext(AuthContext);

    const planPoints = [
        "Unlimited access to all therapy features.",
        "Personalized insights and progress tracking.",
        "24/7 AI powered therapy.",
        "Interactive therapy games designed to support your growth and development."
    ]

    // Determine current plan display
    const getCurrentPlanName = () => {
        if (planStatus === "Beta") {
            return "Beta";
        }
        if (!isSubscribed) {
            return "Basic";
        }
        if (planStatus === "Trial") {
            return "Free Trial";
        }
        if (plan === "monthly") {
            return "Monthly";
        }
        if (plan === "annual" || plan === "yearly") {
            return "Yearly";
        }
        return "Unlimited";
    };

    const getCurrentPlanPrice = () => {
        if (planStatus === "Beta") {
            return "Free (Limited Time)";
        }
        if (!isSubscribed) {
            return "$0";
        }
        if (plan === "monthly") {
            return "$152/month";
        }
        if (plan === "annual" || plan === "yearly") {
            return "$881/year";
        }
        return subScriptionAmount ? `$${subScriptionAmount}` : "N/A";
    };

    return (
        <div className="max-w-4xl mx-auto mt-10">
            {/* Current Plan Card */}
            <div className="bg-white dark:bg-[#222a32] rounded-2xl shadow-lg p-6 sm:p-8 mb-6" style={{ boxShadow: "0px 4px 20px 5px #909090" }}>
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                            Current Plan: IzzyAI {getCurrentPlanName()}
                        </h2>
                        <p className="text-lg text-gray-600 dark:text-gray-400">
                            {getCurrentPlanPrice()}
                        </p>
                    </div>
                    <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
                        planStatus === 'Active' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : planStatus === 'Trial'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : planStatus === 'Beta'
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                    }`}>
                        {planStatus || 'Inactive'}
                    </div>
                </div>

                {/* Plan Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-cyan-600 mt-1 flex-shrink-0" />
                        <div>
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Next Renewal</p>
                            <p className="text-base text-gray-900 dark:text-white">
                                {subScriptionEndDate || planStatus === "Trial" ? subScriptionEndDate : "N/A"}
                            </p>
                        </div>
                    </div>
                    {paymentMethod && (
                        <div className="flex items-start gap-3">
                            <CreditCard className="w-5 h-5 text-cyan-600 mt-1 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Payment Method</p>
                                <p className="text-base text-gray-900 dark:text-white">{paymentMethod}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Plan Features */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        {planStatus === "Beta" 
                            ? "Beta Program Includes:" 
                            : !isSubscribed 
                            ? "Basic Plan Includes:" 
                            : "Your Plan Includes:"}
                    </h3>
                    <ul className="space-y-2">
                        {planStatus === "Beta" ? (
                            <>
                                <li className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>Full access to all therapy features</span>
                                </li>
                                <li className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>Personalized insights and progress tracking</span>
                                </li>
                                <li className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>24/7 AI powered therapy</span>
                                </li>
                                <li className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>Interactive therapy games</span>
                                </li>
                            </>
                        ) : !isSubscribed ? (
                            <li className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                <span>Access to IzzyAI Assessments</span>
                            </li>
                        ) : (
                            planPoints.map((point, index) => (
                                <li key={index} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>{point}</span>
                                </li>
                            ))
                        )}
                    </ul>
                    {planStatus === "Beta" && subScriptionEndDate && (
                        <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                            <p className="text-sm text-purple-800 dark:text-purple-200">
                                <strong>Beta Access:</strong> Your beta program ends on {subScriptionEndDate}. After the beta period, you'll transition to a paid subscription plan.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Upgrade/Buy Options - COMMENTED OUT */}
            {/* 
            <div className="bg-white dark:bg-[#222a32] rounded-2xl shadow-lg p-6 sm:p-8" style={{ boxShadow: "0px 4px 20px 5px #909090" }}>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                    <ArrowUpCircle className="w-6 h-6 text-cyan-600" />
                    {!isSubscribed ? "Subscribe to IzzyAI Unlimited" : "Upgrade Your Plan"}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className={`border-2 rounded-xl p-6 ${
                        plan === "monthly" && isSubscribed
                            ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20"
                            : "border-gray-200 dark:border-gray-700"
                    }`}>
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Monthly Plan</h4>
                            {plan === "monthly" && isSubscribed && (
                                <span className="px-3 py-1 bg-cyan-100 dark:bg-cyan-900 text-cyan-800 dark:text-cyan-200 rounded-full text-xs font-semibold">
                                    Current
                                </span>
                            )}
                        </div>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">$152</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">per month</p>
                        <ul className="space-y-2 mb-4 text-sm text-gray-700 dark:text-gray-300">
                            <li className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <span>7-day free trial</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <span>Cancel anytime</span>
                            </li>
                        </ul>
                        <button
                            onClick={() => handleUpgradeOrBuy("monthly")}
                            disabled={isLoading || (plan === "monthly" && isSubscribed)}
                            className={`w-full py-3 px-4 rounded-lg font-semibold transition-all ${
                                plan === "monthly" && isSubscribed
                                    ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                    : "bg-gradient-to-r from-[#0CC8E8] to-[#2DEEAA] text-white hover:opacity-90 hover:scale-105"
                            }`}
                        >
                            {plan === "monthly" && isSubscribed ? "Current Plan" : isLoading ? "Loading..." : "Select Monthly"}
                        </button>
                    </div>

                    <div className={`border-2 rounded-xl p-6 ${
                        (plan === "annual" || plan === "yearly") && isSubscribed
                            ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20"
                            : "border-gray-200 dark:border-gray-700"
                    }`}>
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Yearly Plan</h4>
                            {(plan === "annual" || plan === "yearly") && isSubscribed && (
                                <span className="px-3 py-1 bg-cyan-100 dark:bg-cyan-900 text-cyan-800 dark:text-cyan-200 rounded-full text-xs font-semibold">
                                    Current
                                </span>
                            )}
                        </div>
                        <div className="flex items-baseline gap-2 mb-2">
                            <p className="text-3xl font-bold text-gray-900 dark:text-white">$881</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">per year</p>
                        </div>
                        <p className="text-sm text-green-600 dark:text-green-400 font-semibold mb-4">
                            Save 17% • Best Value
                        </p>
                        <ul className="space-y-2 mb-4 text-sm text-gray-700 dark:text-gray-300">
                            <li className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <span>All Unlimited features</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <span>Cancel anytime</span>
                            </li>
                        </ul>
                        <button
                            onClick={() => handleUpgradeOrBuy("annual")}
                            disabled={isLoading || ((plan === "annual" || plan === "yearly") && isSubscribed)}
                            className={`w-full py-3 px-4 rounded-lg font-semibold transition-all ${
                                (plan === "annual" || plan === "yearly") && isSubscribed
                                    ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                    : "bg-gradient-to-r from-[#0CC8E8] to-[#2DEEAA] text-white hover:opacity-90 hover:scale-105"
                            }`}
                        >
                            {(plan === "annual" || plan === "yearly") && isSubscribed 
                                ? "Current Plan" 
                                : isLoading 
                                ? "Loading..." 
                                : plan === "monthly" && isSubscribed
                                ? "Upgrade to Yearly"
                                : "Select Yearly"}
                        </button>
                    </div>
                </div>
            </div>
            */}
        </div>
    )
}

export default Plan
